import { Gift } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { PromoType } from '../../types/types';

type PromotionsEmptyProps = ComponentProps<'div'> & {
  promoType: PromoType;
};

/**
 * Renders an empty state for promotions when there are no available offers.
 *
 * @component
 * @param {string} props.promoType - The type of promotion to display the empty state for.
 * @param {string} [props.className] - Additional class names to apply to the container.
 * @returns {JSX.Element} The rendered PromotionsEmpty component.
 */
function PromotionsEmpty({ className, promoType, ...props }: PromotionsEmptyProps) {
  const t = useTranslations('offers.page');
  const hasSubtext = t.has('no_offers_subtext');

  return (
    <div className={twMerge('flex flex-col items-center justify-center py-6', className)} data-testid={`promotions-empty-${promoType}`} {...props}>
      <Gift className="h-16 w-16" />
      <p className="heading-4 text-center pt-2">{t('no_offers', { promoType })}</p>
      {hasSubtext ? <p className="text-center pt-4 max-w-75">{t('no_offers_subtext', { promoType })}</p> : null}
    </div>
  );
}

export { PromotionsEmpty };
